<template>
  <tx-autocomplete
    ref="refAutocomplete"
    :model-value="currentComponentModelValue"
    :clearable="clearable"
    :debounce="appConstants.debounce.input"
    :disabled="disabled || form.MasterSizeScaleId == null || isMasterSizeScaleObsolete"
    :errors="errors"
    :fetch-suggestions="searchSizeScale"
    :placeholder="placeholder"
    :required="required"
    :show-tooltip="true"
    display-prop="SizeScale"
    secondary-display-prop="Sizes"
    value-prop="Id"
    @change="onModelValueSelected"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue'
import { useDebounceFn } from '@vueuse/core'

import type { ErrorObject } from '@vuelidate/core'
import TxAutocomplete from './TxAutocomplete.vue'
import { useUserStore } from '@/store/userData'
import { appConstants } from '@/models/constants'
import utils from '@/services/utils'
import type Article from '@/models/article'
import type { SizeScaleModel } from '@/api/t1/model/sizeModel'

interface IProps {
  articles: Array<Article | Record<string, any>>
  attribute: IAttributeEditorAttribute
  checked?: boolean
  clearable?: boolean
  disabled?: boolean
  errors?: ErrorObject[]
  form: Record<string, any>
  modelValue: number | string | null
  placeholder?: string
  required?: boolean
  showCheckbox?: boolean
  showExternalChangeManagementInfo?: boolean
  showLabel?: boolean
  shouldConsiderObsoleteModelValue?: boolean
  showUpdateSeasonlessAttributeWarning?: boolean
  assignedCommonSizeScaleValue?: number | string | null
}
const props = withDefaults(defineProps<IProps>(), {
  clearable: true,
  disabled: false,
  errors: () => [] as ErrorObject[],
  label: '',
  modelValue: null,
  placeholder: '',
  required: false,
  showCheckbox: false,
  showExternalChangeManagementInfo: false,
  showLabel: true,
  showUpdateSeasonlessAttributeWarning: true,
  shouldConsiderObsoleteModelValue: false,
  assignedCommonSizeScaleValue: null,
})

const emit = defineEmits<{
  (e: 'change', modelValue: any): void
  (e: 'update:modelValue', modelValue: number | string | null): void
}>()

const userStore = useUserStore()

const refAutocomplete = ref<InstanceType<typeof TxAutocomplete>>()
const isInitializing = ref(true)

const currentComponentModelValue = computed({
  get: () => props.modelValue,
  set: (modelValue) => {
    emit('update:modelValue', modelValue)
    // prevent triggering change event when initializing
    if (!isInitializing.value) {
      emit('change', modelValue)
    }
  },
})

const validSizeScales = computed(() => {
  if (props.form.MasterSizeScaleId === null) {
    return []
  }
  if (props.assignedCommonSizeScaleValue && userStore.sizeScales[props.assignedCommonSizeScaleValue] && userStore.sizeScales[props.assignedCommonSizeScaleValue].Sizes !== '') {
    const currentSizes = userStore.sizeScales[props.assignedCommonSizeScaleValue!].Sizes.split(',')
    return Object.values(userStore.sizeScales).filter((sizeScale) => {
      const sizes = sizeScale.Sizes.split(',')
      if (currentSizes.every(v => sizes.includes(v)) && sizes.length > currentSizes.length) {
        return true
      }
      else {
        return false
      }
    })
  }
  else {
    return Object.values(userStore.sizeScales)
  }
})

const isMasterSizeScaleObsolete = computed(() => (props.form.MasterSizeScaleId !== null && !!(userStore.masterSizeScales[props.form.MasterSizeScaleId] && userStore.masterSizeScales[props.form.MasterSizeScaleId].IsObsolete)))

const debouncedUpdateModelFunction = useDebounceFn((modelValue, selectedOption) => {
  let value = modelValue
  // if user remove the value set null
  if (modelValue === '') {
    value = null
  }
  currentComponentModelValue.value = value

  // due to issue with auto complete not being able to set the correct display value when using display-prop, set the initial display value
  if (refAutocomplete.value) {
    refAutocomplete.value.setDisplayModelValue(selectedOption ? selectedOption.SizeScale : '')
  }
}, appConstants.debounce.input)

function onModelValueSelected(value, selectedOption) {
  debouncedUpdateModelFunction(value, selectedOption)
}

function searchSizeScale(query: string, callback) {
  let result: SizeScaleModel[] = []
  if (utils.isDefined(query)) {
    result = validSizeScales.value
      .filter(record => record.MasterSizeScaleId === props.form.MasterSizeScaleId && record.SizeScale != null && ((!props.modelValue && !record.IsObsolete) || ((props.modelValue === record.Id) || !record.IsObsolete)) && record.SizeScale.toString().toLowerCase().includes(query.toString().toLowerCase()))
      .splice(0, 100)
  }
  callback(result)
}

watch(() => props.modelValue, (modelValue) => {
  if (refAutocomplete.value) {
    if (modelValue == null) {
      // if model value changed to null programmatically while validating sizeScale update the display value and make it empty
      refAutocomplete.value.setDisplayModelValue('')
    }
    else { // when sizescale is disabled, on value change we should set the updated value.
      refAutocomplete.value.setDisplayModelValue(modelValue ? userStore.sizeScales[modelValue]?.SizeScale || '' : '')
    }
  }
})

onMounted(async () => {
  await nextTick()
  if (props.modelValue != null && typeof props.modelValue !== 'object') {
    const assignedSizeScale = userStore.sizeScales[props.modelValue]
    currentComponentModelValue.value = assignedSizeScale && (props.shouldConsiderObsoleteModelValue || !assignedSizeScale.IsObsolete) ? assignedSizeScale.Id : null

    // due to issue with auto complete not being able to set the correct display value when using display-prop, set the initial display value
    if (refAutocomplete.value && assignedSizeScale) {
      // Check if the assigned SizeScale is valid based on criteria
      const isValidSizeScale = Object.values(userStore.sizeScales).some(validSize => validSize.Id === assignedSizeScale.Id)

      if (isValidSizeScale && (props.shouldConsiderObsoleteModelValue || !assignedSizeScale.IsObsolete) && props.form.MasterSizeScaleId !== null) {
        currentComponentModelValue.value = assignedSizeScale.Id
        if (refAutocomplete.value) {
          refAutocomplete.value.setDisplayModelValue(assignedSizeScale.SizeScale)
        }
        isInitializing.value = false
      }
      else {
        console.error('Assigned SizeScale is not valid/obsolete.')
        if (!props.disabled) {
          isInitializing.value = false
        }
        currentComponentModelValue.value = null
        if (refAutocomplete.value) {
          refAutocomplete.value.setDisplayModelValue('')
        }
      }
    }
  }
  isInitializing.value = false
})
</script>
